import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  LayoutDashboard,
  Store,
  Package,
  Users,
  Calendar,
  ClipboardList,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Building2,
  Layers,
  Tag,
  FileSpreadsheet,
  Route,
  Eye,
  ShieldCheck,
  BarChart3,
  Sparkles,
  ShieldAlert,
  Target
} from 'lucide-react';

const adminNavItems = [
  { title: 'Dashboard', href: '/', icon: LayoutDashboard },
  { type: 'separator', label: 'Catálogos' },
  { title: 'Cadenas', href: '/chains', icon: Building2 },
  { title: 'Secciones', href: '/sections', icon: Layers },
  { title: 'SKUs', href: '/skus', icon: Package },
  { type: 'separator', label: 'Operaciones' },
  { title: 'Tiendas', href: '/stores', icon: Store },
  { title: 'Usuarios', href: '/users', icon: Users },
  { title: 'Plantillas Ruta', href: '/route-templates', icon: Route },
  { title: 'Asignaciones', href: '/assignments', icon: Calendar },
  { title: 'Carga Masiva', href: '/bulk-upload', icon: FileSpreadsheet },
  { type: 'separator', label: 'Vigilancia IA' },
  { title: 'Panel IA', href: '/ai-monitor', icon: Eye },
  { title: 'Autorizaciones', href: '/deviations', icon: ShieldCheck },
  { title: 'Share-of-Shelf', href: '/share-of-shelf', icon: BarChart3 },
  { title: 'Agente Ejecutivo', href: '/executive-agent', icon: Sparkles },
  { title: 'Motor Autónomo', href: '/rules-engine', icon: ShieldAlert },
  { title: 'Centro Prioridades', href: '/priority-center', icon: Target },
];

const supervisorNavItems = [
  { title: 'Dashboard', href: '/', icon: LayoutDashboard },
  { title: 'Visitas', href: '/visits', icon: ClipboardList },
  { title: 'Alertas', href: '/alerts', icon: Bell },
  { title: 'Panel IA', href: '/ai-monitor', icon: Eye },
  { title: 'Autorizaciones', href: '/deviations', icon: ShieldCheck },
  { title: 'Share-of-Shelf', href: '/share-of-shelf', icon: BarChart3 },
  { title: 'Agente Ejecutivo', href: '/executive-agent', icon: Sparkles },
  { title: 'Motor Autónomo', href: '/rules-engine', icon: ShieldAlert },
  { title: 'Centro Prioridades', href: '/priority-center', icon: Target },
];

export default function Layout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const baseItems = isAdmin ? adminNavItems : supervisorNavItems;
  const monitorItems = isAdmin ? [
    { type: 'separator', label: 'Monitoreo' },
    { title: 'Visitas', href: '/visits', icon: ClipboardList },
    { title: 'Alertas', href: '/alerts', icon: Bell },
  ] : [];
  
  const navItems = [...baseItems, ...monitorItems];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Sidebar */}
      <aside 
        className={cn(
          "bg-slate-900 text-white transition-all duration-300 flex flex-col",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Logo */}
        <div className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <MapPin className="w-5 h-5" />
          </div>
          {!collapsed && (
            <span className="font-bold text-lg font-['Chivo']">MercaWatch</span>
          )}
        </div>

        <Separator className="bg-slate-700" />

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {navItems.map((item, index) => {
              if (item.type === 'separator') {
                return collapsed ? null : (
                  <div key={`sep-${item.label}-${index}`} className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                );
              }
              
              const isActive = location.pathname === item.href || 
                (item.href !== '/' && location.pathname.startsWith(item.href));
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors",
                    isActive 
                      ? "bg-blue-600 text-white" 
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid={`nav-${item.title.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && <span className="text-sm">{item.title}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User info & logout */}
        <div className="p-4 border-t border-slate-700">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full mt-1 inline-block",
                user?.role === 'admin' ? "bg-blue-600/20 text-blue-400" : "bg-emerald-600/20 text-emerald-400"
              )}>
                {user?.role}
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {!collapsed && "Cerrar Sesión"}
          </Button>
        </div>

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="m-2 text-slate-400 hover:text-white"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="collapse-sidebar-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
